import { useId, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import {
  ConversionCTA,
  EASE,
  Reveal,
  RevealItem,
  Section,
  SectionHeader,
  prefersReducedMotion,
} from "@/components/system";

/* ═══════════════════════════════════════════════════════════════════════════
   Services absorbed the old <ICPRouter /> on the homepage.

   Two earlier passes made this section tidier without making it readable. It
   held four stacked blocks — three offerings, three audiences, a three-step
   process, and a closing pitch — roughly 1,500px and 300 words of near-equal
   prose. Ordering that content better did not help, because the problem was
   never the order. It was that all of it was on screen at once, and nobody
   reads a wall no matter how well the wall is organised.

   So this pass cuts and folds instead of rearranging:

     1. THE PROCESS BLOCK IS GONE. The hero already says the same thing three
        words: Strategy / Production / Iteration. <EngagementModel /> carries
        the full version on the nine sub-pages where a reader has actually
        shown intent. Ninety words on the homepage restating the hero is the
        cheapest 350px on the page to give back. (It is one commit to restore
        if it turns out to be load-bearing.)
     2. THE AUDIENCES FOLDED INTO THE OFFERINGS. They were never a second
        axis — "performance creative" and "apps & growth teams" are the same
        row read two ways. Each audience is now the secondary link inside its
        own offering.
     3. THE OFFERINGS BECAME AN ACCORDION. Collapsed, the whole section is
        three lines. That is the entire point: a visitor scanning at speed
        reads three things and picks one, rather than skipping fifteen lines
        of prose. Opening a row is the section's one piece of motion.

   All six routes survive — three titles, three audience links — so nothing was
   traded away for the reduction. Panels stay MOUNTED when closed (height
   animates to 0) so their links remain crawlable; `inert` keeps them out of
   the tab order and the accessibility tree while they are shut.
   ═══════════════════════════════════════════════════════════════════════════ */

const OFFERINGS = [
  {
    title: "Lower your acquisition cost",
    note: "Performance creative for paid social: hooks, scripts, AI production, and weekly iteration against the CAC you already track.",
    href: "/performance-creative",
    hrefLabel: "See performance creative",
    forHref: "/solutions/growth",
    forLabel: "For apps & growth teams",
  },
  {
    title: "Keep people watching",
    note: "Episodic micro drama, long form, and library content for platforms that live and die on retention.",
    href: "/micro-drama-production",
    hrefLabel: "See content systems",
    forHref: "/solutions/platforms",
    forLabel: "For content platforms",
  },
  {
    title: "Make the product story land",
    note: "Brand and product films built for attention, memory, and the next click.",
    href: "/ai-video-production",
    hrefLabel: "See brand & product creative",
    forHref: "/solutions/education",
    forLabel: "For education & consumer brands",
  },
];

const panelLink =
  "group inline-flex min-h-11 items-center gap-2 font-body text-body text-mute " +
  "transition-colors duration-hover ease-gate hover:text-gold";

function Offering({ item, open, onToggle }) {
  const id = useId();
  const reduce = prefersReducedMotion();
  const motionFor = (to) => ({
    animate: to,
    transition: { duration: reduce ? 0 : 0.34, ease: EASE },
  });

  return (
    <li className="border-b border-line">
      <h3>
        <button
          type="button"
          id={`${id}-btn`}
          aria-expanded={open}
          aria-controls={`${id}-panel`}
          onClick={onToggle}
          className="group flex w-full items-center justify-between gap-6 py-7 text-left transition-colors duration-hover ease-gate md:py-8"
        >
          <span
            className={`font-headline text-h2 transition-colors duration-hover ease-gate ${
              open ? "text-bone" : "text-bone group-hover:text-gold"
            }`}
          >
            {item.title}
          </span>

          {/* A plus that becomes a minus. Cheaper than an icon swap, and the
              one bar that moves makes the state change legible rather than
              just different. Gold because the row is a control you can click. */}
          <span
            aria-hidden="true"
            className={`relative block h-4 w-4 shrink-0 transition-colors duration-hover ease-gate ${
              open ? "text-gold" : "text-mute group-hover:text-gold"
            }`}
          >
            <span className="absolute left-0 top-1/2 h-px w-4 -translate-y-1/2 bg-current" />
            <motion.span
              className="absolute left-1/2 top-0 h-4 w-px -translate-x-1/2 bg-current"
              initial={false}
              {...motionFor({ scaleY: open ? 0 : 1 })}
            />
          </span>
        </button>
      </h3>

      <motion.div
        id={`${id}-panel`}
        role="region"
        aria-labelledby={`${id}-btn`}
        inert={!open}
        initial={false}
        {...motionFor({ height: open ? "auto" : 0, opacity: open ? 1 : 0 })}
        className="overflow-hidden"
      >
        <div className="pb-8">
          <p className="measure font-body text-body-l text-mute">{item.note}</p>
          <div className="mt-4 flex flex-wrap items-center gap-x-8">
            <Link to={item.href} className={panelLink}>
              {item.hrefLabel}
              <ArrowRight
                aria-hidden="true"
                className="h-4 w-4 transition-transform duration-hover ease-gate group-hover:translate-x-1"
              />
            </Link>
            <Link to={item.forHref} className={panelLink}>
              {item.forLabel}
              <ArrowRight
                aria-hidden="true"
                className="h-4 w-4 transition-transform duration-hover ease-gate group-hover:translate-x-1"
              />
            </Link>
          </div>
        </div>
      </motion.div>
    </li>
  );
}

export const Services = () => {
  // Everything closed on arrival, one open at a time thereafter. An earlier
  // version opened the first row by default, on the theory that it demonstrated
  // the affordance rather than merely implying it. In practice it just put a
  // paragraph back on screen and made the three rows look uneven — the whole
  // point of the accordion is that the section reads as three headlines. The
  // plus signs carry the affordance on their own.
  const [openIndex, setOpenIndex] = useState(-1);

  return (
    <Section id="services" data-reel-label="What We Make" data-testid="services-section">
      <SectionHeader
        eyebrow="What We Make"
        title="Creative that"
        accent="moves a number."
        lede="Three ways we work, each aimed at a result you already measure. Every one starts with the free sample."
      />

      <Reveal>
        <RevealItem as="ul" className="border-t border-line">
          {OFFERINGS.map((item, i) => (
            <Offering
              key={item.href}
              item={item}
              open={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
            />
          ))}
        </RevealItem>
      </Reveal>

      {/* Deliberately NOT inside a <Reveal>. This is the section's only real
          conversion, and an entrance that fails to fire leaves it at opacity 0 —
          a primary CTA is never worth that risk. */}
      <div className="mt-12 flex flex-col items-start gap-6">
        <p className="measure font-body text-body-l text-mute">
          Bring us a product, campaign, or growth question. We&rsquo;ll show you the
          creative directions we would test first.
        </p>
        <ConversionCTA />
      </div>
    </Section>
  );
};

export default Services;
